<template>
  <div class="ds-page" v-if="!$store.getters.isForbidden">
    <!-- Page Header -->
    <div class="page-header d-print-none">
      <div class="row align-items-center">
        <div class="col">
          <div class="page-pretitle">Management</div>
          <h2 class="page-title">Companies</h2>
          <p class="page-subtitle">Manage registered companies, MyBenefit packages, and related features</p>
        </div>
        <div class="col-auto ms-auto">
          <div class="companies-header-actions">
            <button class="ds-btn ds-btn--secondary" @click="loadCompanies()" title="Refresh">
              <i class="el-icon-refresh"></i>
              Refresh
            </button>
            <button v-if="canCreateCompany" @click="handlerBtnCreate" class="ds-btn ds-btn--primary">
              <font-awesome-icon icon="building" />
              Add Company
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Analytics -->
    <app-user-analytic ref="user_analytic" :is-private="true" />
    <my-benefit-company-analytic :analytic="myben_analytic" />

    <!-- Filter Card -->
    <div class="ds-card">
      <div class="ds-card__body">
        <div class="companies-filter-header">
          <span class="ds-filters__label">Filters</span>
          <span class="ds-filters__hint">Search and filter companies by keyword, status, or MyBenefit period</span>
        </div>

        <div class="companies-filter-two-col">
          <!-- Left: MyBenefit & Status filters -->
          <div class="companies-filter-left">
            <div class="companies-filter-grid-left">
              <div class="ds-field companies-field-full">
                <label class="ds-field__label">MyBenefit Package</label>
                <el-select size="small" v-model="filter.mybenefit_package" @change="searchHandler" class="w-100">
                  <el-option value="" label="All Packages" />
                  <el-option v-for="(pkg, i) in package_list" :key="i" :value="pkg.id.toString()" :label="pkg.package_name" />
                </el-select>
              </div>

              <div class="ds-field">
                <label class="ds-field__label">Status</label>
                <el-select size="small" v-model="filter.status_filter" @change="searchHandler" class="w-100">
                  <el-option v-for="(opt, i) in status_options" :key="i" :value="opt.value" :label="opt.text" />
                </el-select>
              </div>

              <div class="ds-field">
                <label class="ds-field__label">MB Start</label>
                <el-date-picker
                  v-model="filter.myben_start_at"
                  @change="searchHandler"
                  size="small"
                  type="month"
                  placeholder="Select month"
                  class="w-100"
                />
              </div>

              <div class="ds-field">
                <label class="ds-field__label">MB End</label>
                <el-date-picker
                  v-model="filter.myben_end_at"
                  @change="searchHandler"
                  size="small"
                  type="month"
                  placeholder="Select month"
                  class="w-100"
                />
              </div>
            </div>
          </div>

          <!-- Separator -->
          <div class="companies-filter-sep"></div>

          <!-- Right: Search -->
          <div class="companies-filter-right">
            <div class="ds-field">
              <label class="ds-field__label">Search By</label>
              <el-select size="small" v-model="filter.search_by" class="w-100">
                <el-option v-for="(opt, i) in search_options" :key="i" :value="opt.value" :label="opt.text" />
              </el-select>
            </div>

            <div class="ds-field">
              <label class="ds-field__label">Search Keyword</label>
              <el-input
                clearable
                @clear="searchHandler"
                size="small"
                type="text"
                v-model="filter.search_keyword"
                placeholder="Input search keyword..."
              />
            </div>

            <div class="companies-filter-actions">
              <button @click="resetFilter" class="ds-btn ds-btn--secondary ds-btn--sm">Reset</button>
              <button @click="searchHandler" class="ds-btn ds-btn--blue ds-btn--sm">
                <font-awesome-icon icon="search" />
                Search
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Table Card -->
    <div class="ds-card">
      <!-- Table -->
      <div class="ds-table-wrap" v-loading="loading.data">
        <table class="ds-table">
          <thead>
            <tr>
              <th>Company</th>
              <th>Contact</th>
              <th>Status</th>
              <th>MyBenefit</th>
              <th>Effective Date</th>
              <th>Created</th>
              <th>PIC Email</th>
              <th class="text-center">Action</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="item in companies.rows" :key="item.id">
              <td>
                <div class="ds-user-cell">
                  <div class="ds-avatar" :class="dsAvatarVariant(item.company_name)">
                    {{ dsGetInitials(item.company_name) }}
                  </div>
                  <div>
                    <div class="ds-user-cell__name">{{ item.company_name || "—" }}</div>
                  </div>
                </div>
              </td>
              <td>
                <div class="ds-user-cell">
                  <div class="ds-avatar" :class="dsAvatarVariant(item.company_email)">
                    {{ dsGetEmailInitials(item.company_email) }}
                  </div>
                  <div>
                    <div class="ds-user-cell__name">{{ item.company_email || "—" }}</div>
                    <div class="ds-user-cell__sub">{{ item.company_phone || "" }}</div>
                  </div>
                </div>
              </td>
              <td>
                <span class="ds-pill" :class="dsStatusClass(item.status)">
                  {{ dsStatusLabel(item.status) }}
                </span>
              </td>
              <td>
                <div class="ds-myben-cell" v-if="item.mybenefit_package">
                  <img :src="item.mybenefit_package.package_logo" class="ds-myben-logo" />
                  <span>{{ item.mb_package }}</span>
                </div>
                <span v-else class="ds-text-muted">—</span>
              </td>
              <td>
                <div v-if="item.mybenefit_package">
                  <div>{{ item.myben_period }}</div>
                  <span class="ds-pill ds-pill--sm" :class="dsExpiryClass(item.mybenefit_package_end_at)">
                    {{ dsExpiryLabel(item.mybenefit_package_end_at) }}
                  </span>
                </div>
                <span v-else class="ds-text-muted">—</span>
              </td>
              <td>{{ item.created_at_str || "—" }}</td>
              <td>
                <template v-if="item.company_internal_users && item.company_internal_users.length">
                  <div class="ds-user-cell">
                    <div
                      class="ds-avatar"
                      :class="dsAvatarVariant(getLastCompanyInternalUser(item.company_internal_users)?.email)"
                    >
                      {{ dsGetEmailInitials(getLastCompanyInternalUser(item.company_internal_users)?.email) }}
                    </div>
                    <div>
                      <div class="ds-user-cell__name">
                        {{ getLastCompanyInternalUser(item.company_internal_users)?.email || "—" }}
                      </div>
                    </div>
                  </div>
                </template>
                <template v-else>
                  <div class="ds-user-cell">
                    <div class="ds-avatar" :class="dsAvatarVariant(item.pic_email)">
                      {{ dsGetEmailInitials(item.pic_email) }}
                    </div>
                    <div>
                      <div class="ds-user-cell__name">{{ item.pic_email || "—" }}</div>
                    </div>
                  </div>
                </template>
              </td>
              <td class="text-center">
                <el-dropdown @command="handleCommand($event, item)" size="mini">
                  <button class="ds-btn ds-btn--secondary ds-btn--sm ds-btn--icon-right">
                    Action <i class="el-icon-arrow-down"></i>
                  </button>
                  <el-dropdown-menu slot="dropdown" class="companies-dropdown">
                    <el-dropdown-item command="handlerBtnEditCompany">
                      <font-awesome-icon icon="edit" class="companies-dropdown__icon" />
                      Edit Company Data
                    </el-dropdown-item>

                    <el-dropdown-item command="handlerBtnDeactivateCompany">
                      <font-awesome-icon
                        :icon="item.status === 'active' ? 'toggle-off' : 'toggle-on'"
                        class="companies-dropdown__icon"
                      />
                      {{ item.status === "active" ? "Deactivate Company" : "Activate Company" }}
                    </el-dropdown-item>

                    <el-dropdown-item
                      command="handlerBtnApproveCompany"
                      :disabled="item.status !== 'waiting_approval' || !canApproveCompany"
                    >
                      <font-awesome-icon icon="check" class="companies-dropdown__icon" />
                      Approve
                    </el-dropdown-item>

                    <li class="companies-dropdown__divider" />

                    <el-dropdown-item command="handlerBtnMyBenefit" :disabled="item.status === 'not_active'">
                      <font-awesome-icon icon="gift" class="companies-dropdown__icon" />
                      My Benefit
                    </el-dropdown-item>

                    <el-dropdown-item command="handlerBtnMyBenefitRevamp" :disabled="item.status === 'not_active'">
                      <font-awesome-icon icon="gift" class="companies-dropdown__icon" />
                      My Benefit Revamp
                    </el-dropdown-item>

                    <el-dropdown-item command="handlerUnsubscribeMyBenefit" :disabled="!item.mybenefit_package_id">
                      <font-awesome-icon icon="times-circle" class="companies-dropdown__icon" />
                      Unsubscribe MyBenefit
                    </el-dropdown-item>

                    <li class="companies-dropdown__divider" />

                    <el-dropdown-item command="handlerBtnNewCompanyPage" v-if="isAllowed">
                      <font-awesome-icon icon="building" class="companies-dropdown__icon" />
                      Company Page
                    </el-dropdown-item>

                    <el-dropdown-item
                      command="handlerBtnPrivateUser"
                      :disabled="item.status === 'waiting_approval' || item.status === 'not_active'"
                    >
                      <font-awesome-icon icon="mobile-alt" class="companies-dropdown__icon" />
                      Private Users
                    </el-dropdown-item>

                    <el-dropdown-item command="handlerBtnEwa" :disabled="item.status === 'waiting_approval'">
                      <font-awesome-icon icon="money-bill-wave" class="companies-dropdown__icon" />
                      EWA
                    </el-dropdown-item>

                    <el-dropdown-item command="handlerCompanyClient">
                      <font-awesome-icon icon="users" class="companies-dropdown__icon" />
                      Manage Client
                    </el-dropdown-item>

                    <li class="companies-dropdown__divider" />

                    <el-dropdown-item command="handlerBtnBalanceHistories" :disabled="!item.mbayar_enabled">
                      <font-awesome-icon icon="history" class="companies-dropdown__icon" />
                      Wallet Histories
                    </el-dropdown-item>

                    <el-dropdown-item command="handlerBtnCashout" :disabled="!item.mbayar_enabled">
                      <font-awesome-icon icon="cash-register" class="companies-dropdown__icon" />
                      Withdrawal
                    </el-dropdown-item>

                    <li class="companies-dropdown__divider" />

                    <el-dropdown-item command="handlerBtnDeleteCompany">
                      <font-awesome-icon icon="trash" class="companies-dropdown__icon companies-dropdown__icon--danger" />
                      <span class="companies-dropdown__label--danger">Delete Company</span>
                    </el-dropdown-item>
                  </el-dropdown-menu>
                </el-dropdown>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <el-empty v-if="!companies.count" description="No companies found" :image="emptyStateImage" class="ds-empty" />

      <pagination
        :page="filter.page"
        :per_page="filter.per_page"
        :list="companies.rows"
        :total_rows="companies.count"
        @pageChange="pageChangeHandler"
      />
    </div>

    <!-- Modals -->
    <create-company
      :is-shown="showModalCreateCompany"
      @onHide="showModalCreateCompany = false"
      :selected-company="selected_company"
      :modal-type="modal_type"
      @afterSubmit="loadCompanies"
    />
    <my-benefit
      :is-shown="showModalMyBenefit"
      :selected-company="selected_company"
      @onHide="
        showModalMyBenefit = false;
        loadCompanies();
      "
    />
    <my-benefit-revamp
      :is-shown="showModalMyBenefitRevamp"
      :selected-company="selected_company"
      @onHide="
        showModalMyBenefitRevamp = false;
        loadCompanies();
      "
    />
    <balance-histories
      :is-shown="showModalBalanceHistories"
      :selected-company="selected_company"
      @onHide="showModalBalanceHistories = false"
    />
    <cashout :is-shown="showModalCashout" :selected-company="selected_company" @onHide="showModalCashout = false" />
  </div>
</template>

<script>
/* eslint-disable radix */
import moment from "moment";
import { isEmpty, isEqual, clone } from "lodash";
import { GET_COMPANIES, APPROVE_CREATE_COMPANY, DELETE_COMPANIES, DEACTIVATE_COMPANY } from "@/store/modules/companies";
import { GET_MYBENEFIT, ANALYTIC_MYBENEFIT_COMPANY, UNASSIGN_PACKAGE } from "@/store/modules/mybenefit";
import { designSystemMixin } from "@/mixins/designSystemMixin";
import emptyStateImage from "@/assets/images/empty-state.png";
import CreateCompany from "./companies/CreateCompany.vue";
import MyBenefit from "./companies/MyBenefit.vue";
import MyBenefitRevamp from "./companies/MyBenefitRevamp.vue";
import BalanceHistories from "./companies/BalanceHistories.vue";
import Cashout from "./companies/Cashout.vue";
import popupErrorMessages from "../../library/popup-error-messages";
import Pagination from "../components/Pagination.vue";
import AppUserAnalytic from "./app_user/Analytic.vue";
import MyBenefitCompanyAnalytic from "./mybenefit/CompanyAnalytic.vue";

export default {
  name: "Companies",
  mixins: [designSystemMixin],
  components: {
    CreateCompany,
    MyBenefit,
    MyBenefitRevamp,
    BalanceHistories,
    Cashout,
    Pagination,
    AppUserAnalytic,
    MyBenefitCompanyAnalytic,
  },
  metaInfo: {
    title: "Companies",
  },
  data() {
    return {
      emptyStateImage,
      showModalCreateCompany: false,
      showModalMyBenefit: false,
      showModalMyBenefitRevamp: false,
      showModalBalanceHistories: false,
      showModalCashout: false,
      loading: {
        data: false,
      },
      filter: {
        search_keyword: "",
        search_by: "company_name",
        mybenefit_package: "",
        status_filter: "",
        myben_start_at: "",
        myben_end_at: "",
        page: 1,
        per_page: 10,
      },
      search_options: [
        { value: "company_name", text: "Company Name" },
        { value: "company_email", text: "Company Email" },
        { value: "company_phone", text: "Company Phone" },
      ],
      status_options: [
        { value: "", text: "All Status" },
        { value: "active", text: "Active" },
        { value: "waiting_approval", text: "Waiting Approval" },
      ],
      companies: {
        rows: [],
        count: 0,
      },
      selected_company: {},
      modal_type: "add",
      package_list: [],
      myben_analytic: [],
    };
  },
  async mounted() {
    if (!isEmpty(this.$route.query)) {
      this.filter = clone(this.$route.query);
      this.filter.page = this.$route.query.page ? parseInt(this.$route.query.page) : 1;
      this.filter.per_page = this.$route.query.per_page ? parseInt(this.$route.query.per_page) : 10;
    }
    const loader = this.$loading.show();
    await this.loadCompanies();
    this.loadMyBenefitList();
    this.myBenefitAnalytic();
    loader.hide();
  },
  computed: {
    currentRole() {
      return this.$store.getters.current_role || {};
    },
    currentMyProfile() {
      return this.$store.getters.currentMyProfile || {};
    },
    isAllowed() {
      const roleName = this.currentRole?.role?.name;
      return roleName === "CRM" || roleName === "Super Admin" || roleName === "OPS Team";
    },
    canCreateCompany() {
      return Boolean(this.currentRole.can_create || this.currentRole.page_name === "*" || this.currentMyProfile.is_maker);
    },
    canEditCompany() {
      return Boolean(this.currentRole.can_edit || this.currentRole.page_name === "*");
    },
    canApproveCompany() {
      return Boolean(this.currentRole.can_edit || this.currentRole.page_name === "*" || this.currentMyProfile.is_checker);
    },
  },
  methods: {
    handlerBtnCreate() {
      if (!this.canCreateCompany) {
        this.$message.error("You do not have permission to create a company.");
        return;
      }
      this.modal_type = "add";
      this.selected_company = {};
      this.showModalCreateCompany = true;
    },
    handlerBtnEditCompany(item) {
      this.selected_company = item;
      this.modal_type = "edit";
      this.showModalCreateCompany = true;
    },
    handlerBtnMyBenefit(item) {
      this.selected_company = item;
      this.showModalMyBenefit = true;
    },
    handlerBtnMyBenefitRevamp(item) {
      this.selected_company = item;
      this.showModalMyBenefitRevamp = true;
    },
    handlerBtnNewCompanyPage(item) {
      this.$router.push({
        name: "Company Detail",
        params: { id: item.company_id },
      });
    },
    handlerBtnPrivateUser(item) {
      if (item.ewa_tier !== null && item.ewa_tier !== undefined) {
        try {
          localStorage.setItem("ewa_tier", JSON.stringify(item.ewa_tier));
        } catch (e) {
          console.error("Error saving ewa_tier to local storage:", e);
        }
      } else {
        localStorage.removeItem("ewa_tier");
      }
      this.$router.push({
        name: "Companies Private User",
        params: { id: item.company_id },
      });
    },
    handlerCompanyClient(item) {
      this.$router.push({
        name: "Company Client List",
        params: { id: item.company_id },
        query: { company_name: item.company_name || "" },
      });
    },
    handlerBtnEwa(item) {
      this.$router.push({
        name: "Companies EWA",
        params: { id: item.company_id },
      });
    },
    handlerBtnBalanceHistories(item) {
      this.selected_company = item;
      this.showModalBalanceHistories = true;
    },
    handlerBtnCashout(item) {
      this.selected_company = item;
      this.showModalCashout = true;
    },
    handlerUnsubscribeMyBenefit(item) {
      this.$confirm("Are you sure want to unsubscribe MyBenefit for this company?", "Confirmation", {
        type: "info",
        confirmButtonText: "Yes",
        beforeClose: async (action, instance, done) => {
          if (action === "confirm") {
            instance.confirmButtonLoading = true;
            try {
              await this.$store.dispatch(UNASSIGN_PACKAGE, { id: item.company_id });
              this.$message({ type: "success", message: "MyBenefit removed" });
              this.loadCompanies();
            } catch (err) {
              popupErrorMessages(err?.response?.data);
            } finally {
              instance.confirmButtonLoading = false;
              done();
            }
          } else {
            done();
          }
        },
      }).catch(() => {});
    },
    handlerBtnApproveCompany(item) {
      if (!this.canApproveCompany) {
        this.$message.error("You do not have permission to approve a company.");
        return;
      }
      this.$confirm("Are you sure you want to approve this company?", "Confirmation", {
        type: "info",
        confirmButtonText: "Yes",
        beforeClose: async (action, instance, done) => {
          if (action === "confirm") {
            instance.confirmButtonLoading = true;
            try {
              await this.$store.dispatch(APPROVE_CREATE_COMPANY, {
                id: item.company_id,
                data: {},
              });
              this.$message({ type: "success", message: "Approval success" });
              this.loadCompanies();
            } catch (err) {
              popupErrorMessages(err?.response?.data);
            } finally {
              instance.confirmButtonLoading = false;
              done();
            }
          } else {
            done();
          }
        },
      }).catch(() => {});
    },
    handlerBtnDeleteCompany(item) {
      this.$confirm("Are you sure you want to delete this company?", "Confirmation", {
        type: "warning",
        confirmButtonText: "Yes, delete",
        beforeClose: async (action, instance, done) => {
          if (action === "confirm") {
            instance.confirmButtonLoading = true;
            try {
              await this.$store.dispatch(DELETE_COMPANIES, {
                id: item.company_id,
                data: {},
              });
              this.$message({ type: "success", message: "Company deleted" });
              this.loadCompanies();
            } catch (err) {
              popupErrorMessages(err?.response?.data);
            } finally {
              instance.confirmButtonLoading = false;
              done();
            }
          } else {
            done();
          }
        },
      }).catch(() => {});
    },
    handlerBtnDeactivateCompany(item) {
      const isActive = item.status === "active";
      const nextStatus = isActive ? "not_active" : "active";
      const confirmMessage = isActive
        ? "Are you sure you want to deactivate this company? This will disable access and related features."
        : "Are you sure you want to activate this company?";

      this.$confirm(confirmMessage, "Confirmation", {
        type: "info",
        confirmButtonText: "Yes",
        beforeClose: async (action, instance, done) => {
          if (action === "confirm") {
            instance.confirmButtonLoading = true;
            try {
              await this.$store.dispatch(DEACTIVATE_COMPANY, {
                id: item.company_id,
                data: { status: nextStatus },
              });
              this.$message({
                type: "success",
                message: isActive ? "Company deactivated" : "Company activated",
              });
              this.loadCompanies();
            } catch (err) {
              popupErrorMessages(err?.response?.data);
            } finally {
              instance.confirmButtonLoading = false;
              done();
            }
          } else {
            done();
          }
        },
      }).catch(() => {});
    },
    handleCommand(fn, item) {
      this[fn](item);
    },
    searchHandler() {
      this.filter.page = 1;
      if (this.filter.myben_start_at) {
        this.filter.myben_start_at = moment(this.filter.myben_start_at).format("YYYY-MM-DD");
      }
      if (this.filter.myben_end_at) {
        this.filter.myben_end_at = moment(this.filter.myben_end_at).format("YYYY-MM-DD");
      }
      this.loadCompanies();
    },
    resetFilter() {
      this.filter.search_keyword = "";
      this.filter.search_by = "company_name";
      this.filter.mybenefit_package = "";
      this.filter.status_filter = "";
      this.filter.myben_start_at = "";
      this.filter.myben_end_at = "";
      this.filter.page = 1;
      this.loadCompanies();
    },
    pageChangeHandler(page) {
      this.filter.page = page;
      this.updateURLQuery();
    },
    updateURLQuery() {
      if (!isEqual(this.$route.query, this.filter)) {
        this.$router
          .replace({
            name: this.$route.name,
            query: { ...this.filter },
          })
          .catch(() => {});
      }
    },
    async loadCompanies() {
      this.loading.data = true;
      this.updateURLQuery();
      try {
        await this.$store.dispatch(GET_COMPANIES, {
          page: this.filter.page,
          ...this.filter,
        });
        this.companies = this.$store.getters.companies || { rows: [], count: 0 };
      } finally {
        this.loading.data = false;
      }
    },
    async loadMyBenefitList() {
      try {
        const response = await this.$store.dispatch(GET_MYBENEFIT);
        this.package_list = response?.rows || [];
      } catch {
        this.package_list = [];
      }
    },
    async myBenefitAnalytic() {
      try {
        let { rows } = await this.$store.dispatch(ANALYTIC_MYBENEFIT_COMPANY);
        rows = (rows || []).map((v) => ({ count: v.count, ...v.mybenefit_package }));
        this.myben_analytic = rows;
      } catch {
        this.myben_analytic = [];
      }
    },
    getLastCompanyInternalUser(users) {
      if (!users || users.length === 0) return null;
      return users[users.length - 1];
    },
  },
};
</script>

<style scoped>
/* ── Page Header Actions ─────────────────────────────────── */
.companies-header-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

/* ── Filter Card ─────────────────────────────────────────── */
.companies-filter-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 14px;
}
.companies-filter-two-col {
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  gap: 0 20px;
  align-items: start;
}
.companies-filter-grid-left {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px 12px;
  align-items: end;
}
.companies-field-full {
  grid-column: span 2;
}
.companies-filter-sep {
  width: 1px;
  background: rgba(101, 109, 119, 0.12);
  align-self: stretch;
  margin-top: 2px;
}
.companies-filter-right {
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.companies-filter-actions {
  display: flex;
  gap: 8px;
  justify-content: flex-end;
  margin-top: 4px;
}

/* ── MyBenefit logo cell ─────────────────────────────────── */
.ds-myben-cell {
  display: flex;
  align-items: center;
  gap: 8px;
}
.ds-myben-logo {
  width: 28px;
  height: 28px;
  object-fit: contain;
  border-radius: 4px;
}

/* ── Muted text helper ───────────────────────────────────── */
.ds-text-muted {
  font-size: 11.5px;
  color: #8c98a4;
}

/* ── Pill small variant ──────────────────────────────────── */
.ds-pill.ds-pill--sm {
  font-size: 11px;
  font-weight: 600;
  padding: 2px 10px;
  border-radius: 999px;
}
/* Override pill colors to soft-fill style (matching mockup) */
.ds-pill.ds-pill--sm.ds-pill--danger {
  background: rgba(214, 57, 57, 0.12);
  color: #b91c1c;
  border: none;
}
.ds-pill.ds-pill--sm.ds-pill--warning {
  background: rgba(240, 160, 0, 0.12);
  color: #b45309;
  border: none;
}
.ds-pill.ds-pill--sm.ds-pill--success {
  background: rgba(47, 179, 68, 0.14);
  color: #166534;
  border: none;
}
.ds-pill.ds-pill--sm.ds-pill--gray {
  background: rgba(101, 109, 119, 0.1);
  color: #656d77;
  border: none;
}

/* ── Action Dropdown ─────────────────────────────────────── */
.companies-dropdown {
  min-width: 200px;
}
.companies-dropdown__icon {
  width: 14px;
  margin-right: 8px;
  color: #656d77;
}
.companies-dropdown__icon--danger {
  color: #d63939;
}
.companies-dropdown__label--danger {
  color: #d63939;
}
.companies-dropdown__divider {
  list-style: none;
  margin: 4px 0;
  border-top: 1px solid rgba(101, 109, 119, 0.12);
}
</style>
